import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { useGetMovingPrintPreviewMutation } from '@/store/api/saleControlApi'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: rpCtrlSalesMoving_Many_1..5 (Lệnh điều chuyển nội bộ / Internal
// Moving). 1..5 selected moving orders → 1 dialog, mỗi record 1 trang.
// Mirror DoPrintDialog (iframe srcDoc preview + Print → printAndUploadReport),
// nhưng nhận `moveNos: string[]` và TỰ fetch HTML khi mở qua
// useGetMovingPrintPreviewMutation.

export default function MovingPrintDialog({
  open,
  moveNos,
  canEdit = true,
  onClose,
}: {
  open: boolean
  moveNos: string[]
  canEdit?: boolean
  onClose: () => void
}) {
  const [toastMessage, setToastMessage] = useState('')
  const [fetchPreview, { data, isLoading, reset }] = useGetMovingPrintPreviewMutation()

  const html = data?.html ?? ''
  const iframeTitle = 'Moving Print Preview'
  const previewHtml = html
    ? buildPrintPreviewHtml(html, {
        styleId: 'moving-print-preview-overrides',
        extraCss: '.access-report { margin: 0 auto 6mm !important; }',
      })
    : ''

  // On open with non-empty moveNos → trigger mutation once. Reset on close.
  useEffect(() => {
    if (open && moveNos.length > 0) {
      void fetchPreview(moveNos)
    }
    if (!open) {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, moveNos.join('|')])

  // Tên file PDF lấy theo list move_no (nối bằng "_"); 1 dòng → chính move_no đó.
  const idPart = moveNos.join('_')

  return (
    <>
      {open && isLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={data?.title || 'Moving'}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            Moving
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const printTitle = `${sanitizePdfFileNamePart(idPart || 'moving')}.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesMoving',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
